import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Upload,
  FileText,
  Loader2,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Info,
  Mail,
  Download,
  Copy,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { bankFromIfsc } from "@/lib/india";
import RbiBadge from "@/components/RbiBadge";
import { WITHDRAW } from "@/constants/testIds";
import { usePlatformFee } from "@/hooks/usePlatformFee";

const inr = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(v);
const usd = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v);

// RBI purpose codes — auto-mapped from payment_type. Mirrors backend
// services/purpose_codes.py exactly. Displayed as read-only confirmation.
const PURPOSE_CODES = {
  advance: {
    code: "P0103",
    description:
      "Advance receipts against export contracts, covered later by GR/PP/SOFTEX/SDF",
  },
  balance: {
    code: "P0102",
    description:
      "Realisation of export bills (in respect of goods) sent on collection",
  },
};
const purposeCodeFor = (paymentType) =>
  PURPOSE_CODES[(paymentType || "balance").toLowerCase()] || PURPOSE_CODES.balance;

export default function WithdrawalWizard() {
  const { id: txnId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const feePct = usePlatformFee(0.005);

  const [step, setStep] = useState("upload"); // upload | review | confirm | processing | done
  const [txn, setTxn] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [uploadId, setUploadId] = useState("");
  const [extractionMeta, setExtractionMeta] = useState(null);
  const [paymentType, setPaymentType] = useState("balance"); // "advance" | "balance"
  const [fields, setFields] = useState({
    buyer_name: "",
    shipping_bill_number: "",
    // Both DGFT compliance fields — no separate "invoice_number" / "invoice_date"
    gst_invoice_number: "",
    gst_invoice_date: "",
    invoice_amount: "",
  });
  const [result, setResult] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [payoutInfo, setPayoutInfo] = useState(null); // from /dashboard for the confirm step

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, dash] = await Promise.all([
          api.get(`/transactions/${txnId}`),
          api.get(`/dashboard`).catch(() => ({ data: null })),
        ]);
        setTxn(data);
        if (dash?.data?.payout) setPayoutInfo(dash.data.payout);
        if (data.withdrawal_status === "complete") {
          toast.info("This deposit has already been withdrawn.");
          navigate(`/transactions/${txnId}`, { replace: true });
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load transaction.");
        navigate("/dashboard");
      }
    })();
  }, [txnId, navigate]);

  const onFilePicked = (f) => {
    if (!f) return;
    const ok = ["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(f.type);
    if (!ok) {
      toast.error("Only PDF, JPEG or PNG files are accepted.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8 MB.");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose an invoice file to upload.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("sf_token");
      const resp = await fetch(`${API}/transactions/${txnId}/withdrawal/upload-invoice`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Upload failed");

      setUploadId(data.upload_id);
      setExtractionMeta(data.extraction);
      const f = data.extraction?.fields || {};
      setFields((prev) => ({
        ...prev,
        buyer_name: f.buyer_name || "",
        // Populate BOTH DGFT compliance fields from the AI extraction
        gst_invoice_number: f.invoice_number || "",
        gst_invoice_date: f.invoice_date || "",
        invoice_amount: f.invoice_amount ? String(f.invoice_amount) : "",
        // Pre-fill shipping bill from a prior withdrawal against same buyer+invoice
        shipping_bill_number: data.prior_shipping_bill_number || prev.shipping_bill_number || "",
      }));
      setStep("review");

      if (data.prior_shipping_bill_number) {
        toast.info(`Shipping bill pre-filled from a previous withdrawal for this invoice.`);
      }
      if (data.extraction?.ok) {
        toast.success("Invoice parsed — please review the details.");
      } else {
        toast.warning("Automatic extraction unavailable — please fill the fields.");
      }
    } catch (err) {
      toast.error(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Step 2 → Step 3 (confirmation screen): validate only, do NOT hit the API yet.
  const goToConfirmation = () => {
    const miss = [];
    if (!fields.gst_invoice_number.trim()) miss.push("GST Invoice Number");
    if (!fields.gst_invoice_date.trim()) miss.push("GST Invoice Date");
    if (!fields.buyer_name.trim()) miss.push("Buyer name");
    if (!fields.invoice_amount || Number(fields.invoice_amount) <= 0) miss.push("Invoice amount");
    if (miss.length) {
      toast.error(`Please fill: ${miss.join(", ")}`);
      return;
    }
    if (paymentType === "balance" && !fields.shipping_bill_number.trim()) {
      toast.error("Shipping Bill Number is required for balance payments");
      return;
    }
    setStep("confirm");
  };

  const confirm = async () => {
    setConfirming(true);
    setStep("processing");
    try {
      const { data } = await api.post(`/transactions/${txnId}/withdrawal/confirm`, {
        upload_id: uploadId,
        // GST invoice fields double as both compliance + reconciliation record.
        invoice_number: fields.gst_invoice_number.trim(),
        invoice_amount: Number(fields.invoice_amount || 0),
        invoice_date: fields.gst_invoice_date.trim(),
        buyer_name: fields.buyer_name.trim(),
        payment_type: paymentType,
        shipping_bill_number:
          paymentType === "balance" ? fields.shipping_bill_number.trim() : null,
        gst_invoice_number: fields.gst_invoice_number.trim(),
        gst_invoice_date: fields.gst_invoice_date.trim(),
      });
      setResult(data);
      setStep("done");
      toast.success("Withdrawal complete — eFIRA is now available.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete withdrawal.");
      setStep("confirm");
    } finally {
      setConfirming(false);
    }
  };

  const bankName = bankFromIfsc(payoutInfo?.ifsc);

  // ---- Email draft (client-side assembly) --------------------------------
  const emailSubject = () =>
    `EDPMS Disposal Instructions — UTR ${result?.payout?.reference || txn?.id || ""}`;
  const emailBody = () => {
    const utr = result?.payout?.reference || txn?.id || "";
    const amountUsd = txn ? usd(txn.amount_usd) : "";
    const amountInr = result?.payout?.amount_inr ? inr(result.payout.amount_inr) : "";
    const valueDate = txn?.settlement_date ? new Date(txn.settlement_date).toLocaleDateString("en-IN") : "";
    const remitter = fields.buyer_name || txn?.sender_name || "";
    const sb = fields.shipping_bill_number || "—";
    const biz = user?.profile?.legal_name || user?.email || "";
    return (
      `Dear ${bankName} Trade Finance Team,\n\n` +
      "We have received a foreign inward remittance with the following details:\n\n" +
      `UTR Number: ${utr}\n` +
      `Amount: USD ${amountUsd.replace("$", "")} / INR ${amountInr.replace("₹", "")}\n` +
      `Value Date: ${valueDate}\n` +
      `Remitter: ${remitter}\n\n` +
      `Please knock off the above IRM against Shipping Bill Number: ${sb}.\n\n` +
      "The JP Morgan India Payment Advice is attached for your reference.\n\n" +
      "Regards,\n" +
      `${biz}`
    );
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${emailSubject()}\n\n${emailBody()}`);
      toast.success("Email copied to clipboard");
    } catch {
      toast.error("Copy failed — please select and copy manually.");
    }
  };

  const downloadAdvice = async () => {
    try {
      const token = localStorage.getItem("sf_token");
      const res = await fetch(`${API}/transactions/${txnId}/efira`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Flo Payments-Payment-Advice-${txnId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Payment Advice downloaded");
    } catch {
      toast.error("Could not download Payment Advice");
    }
  };

  if (!txn) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid={WITHDRAW.root}>
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto flex items-center gap-2 p-4 md:p-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/transactions/${txnId}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to transaction
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 md:p-10 space-y-6">
        <RbiBadge />
        <div>
          <p className="text-sm text-muted-foreground mb-1">Withdrawal for</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">
            {usd(txn.amount_usd)} from {txn.sender_name}
          </h1>
        </div>

        <Stepper step={step} />

        {step === "upload" && (
          <Card>
            <CardContent className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="font-display text-xl font-bold">Upload the sales invoice</h2>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  {"PDF, JPEG or PNG · max 8 MB. We'll auto-extract the details for you to confirm."}
                </p>
              </div>

              <label
                className="block border-2 border-dashed border-border rounded-xl p-10 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid={WITHDRAW.dropzone}
              >
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="sr-only"
                  onChange={(e) => onFilePicked(e.target.files?.[0])}
                  data-testid={WITHDRAW.fileInput}
                />
                {!file ? (
                  <>
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="font-medium text-foreground">Click to choose a file</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF / JPEG / PNG</p>
                  </>
                ) : (
                  <>
                    <FileText className="h-8 w-8 mx-auto text-primary mb-3" />
                    <p className="font-medium text-foreground">{file.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {(file.size / 1024).toFixed(1)} KB · click to change
                    </p>
                  </>
                )}
              </label>

              <Button
                onClick={upload}
                disabled={!file || uploading}
                className="w-full h-14 font-semibold rounded-lg"
                data-testid={WITHDRAW.uploadBtn}
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Extracting invoice…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" /> Extract with AI
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "review" && (
          <Card>
            <CardContent className="p-6 md:p-8 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl font-bold">Review invoice details</h2>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">
                    {extractionMeta?.ok
                      ? "We've pre-filled the details from your invoice. Please review and correct anything below."
                      : "Please enter the invoice details below."}
                  </p>
                  {file && (
                    <p className="text-xs text-muted-foreground mt-2 truncate max-w-md">
                      <FileText className="inline h-3 w-3 mr-1 -mt-0.5" />
                      {file.name}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setUploadId("");
                    setExtractionMeta(null);
                    setFields({
                      buyer_name: "",
                      shipping_bill_number: "",
                      gst_invoice_number: "",
                      gst_invoice_date: "",
                      invoice_amount: "",
                    });
                    setStep("upload");
                    toast.info("Uploaded a wrong file? Pick a different one.");
                  }}
                  data-testid="withdraw-reupload-btn"
                  className="shrink-0"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> Choose a different file
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <FieldRow
                  label="Buyer name"
                  value={fields.buyer_name}
                  onChange={(v) => setFields((f) => ({ ...f, buyer_name: v }))}
                  testid={WITHDRAW.buyerName}
                  hint="Overseas buyer as named on the sales invoice"
                />
                <FieldRow
                  label="Invoice amount (USD)"
                  value={fields.invoice_amount}
                  onChange={(v) => setFields((f) => ({ ...f, invoice_amount: v }))}
                  testid={WITHDRAW.invoiceAmount}
                  type="number"
                />
              </div>

              <div className="pt-2 border-t">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-4">
                  DGFT / eBRC self-certification (required Jan 2026 onwards)
                </p>

                {/* Payment type toggle — drives conditional Shipping Bill field */}
                <div className="mb-6 space-y-3">
                  <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Payment type <span className="text-red-500">*</span>
                  </Label>
                  <label
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      paymentType === "advance"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_type"
                      value="advance"
                      checked={paymentType === "advance"}
                      onChange={() => setPaymentType("advance")}
                      className="mt-1 accent-[hsl(var(--primary))]"
                      data-testid={WITHDRAW.paymentTypeAdvance}
                    />
                    <div>
                      <p className="font-medium text-foreground">
                        Advance payment — goods not yet shipped
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Shipping Bill Number is not required now.
                      </p>
                    </div>
                  </label>

                  <label
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      paymentType === "balance"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_type"
                      value="balance"
                      checked={paymentType === "balance"}
                      onChange={() => setPaymentType("balance")}
                      className="mt-1 accent-[hsl(var(--primary))]"
                      data-testid={WITHDRAW.paymentTypeBalance}
                    />
                    <div>
                      <p className="font-medium text-foreground">
                        Balance / final payment — goods already shipped
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Shipping Bill Number is required to close the EDPMS entry.
                      </p>
                    </div>
                  </label>
                </div>

                {paymentType === "advance" && (
                  <div className="flex gap-3 rounded-lg bg-muted p-4 border mb-6">
                    <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      When your goods ship, share the shipping bill number with your HDFC Bank trade finance team
                      to close your EDPMS entry.
                    </p>
                  </div>
                )}

                <div className="grid md:grid-cols-3 gap-6">
                  {paymentType === "balance" && (
                    <FieldRow
                      label="Shipping Bill Number"
                      value={fields.shipping_bill_number}
                      onChange={(v) => setFields((f) => ({ ...f, shipping_bill_number: v }))}
                      testid={WITHDRAW.shippingBill}
                      hint="Issued by ICEGATE at export clearance (format: SB/YYYY/PORT/NNNNNN)"
                    />
                  )}
                  <FieldRow
                    label="GST Invoice Number"
                    value={fields.gst_invoice_number}
                    onChange={(v) => setFields((f) => ({ ...f, gst_invoice_number: v }))}
                    testid={WITHDRAW.gstInvoiceNumber}
                    hint="As shown on your tax invoice"
                  />
                  <FieldRow
                    label="GST Invoice Date"
                    value={fields.gst_invoice_date}
                    onChange={(v) => setFields((f) => ({ ...f, gst_invoice_date: v }))}
                    testid={WITHDRAW.gstInvoiceDate}
                    placeholder="YYYY-MM-DD"
                    type="date"
                  />
                </div>
              </div>

              <Button
                onClick={goToConfirmation}
                className="w-full h-14 font-semibold rounded-lg"
                data-testid={WITHDRAW.confirmBtn}
              >
                Review &amp; confirm <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "confirm" && txn && (
          <Card data-testid="withdraw-confirm-screen">
            <CardContent className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="font-display text-2xl font-bold text-primary">Confirm your withdrawal</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Please review before we initiate your payout
                </p>
              </div>

              {(() => {
                // Fee % comes from usePlatformFee → /api/platform/fee — same
                // value the FX widget uses and the admin fee page controls.
                const rate = txn.fx_rate || 0;
                const gross = (Number(fields.invoice_amount) || txn.amount_usd) * rate;
                const fee = gross * feePct;
                const net = Math.max(0, gross - fee);
                const feeDisplay = +((feePct * 100).toFixed(3));
                const pc = purposeCodeFor(paymentType);
                return (
                  <div className="space-y-3 text-sm">
                    <SummaryRow label="Payout amount" value={inr(gross)} mono />
                    <SummaryRow label="To bank" value={bankName} />
                    <SummaryRow label="Account" value={payoutInfo?.number_masked} mono />
                    <SummaryRow label="IFSC" value={payoutInfo?.ifsc} mono />
                    <SummaryRow
                      label="Flo Payments rate applied"
                      value={`₹${rate.toFixed(4)} per $`}
                      sublabel="(Google mid-market rate — no markup)"
                      mono
                    />
                    <SummaryRow label={`Flo Payments fee (${feeDisplay}%)`} value={inr(fee)} mono />
                    <SummaryRow label="You receive" value={inr(net)} mono />
                    <div
                      className="pt-3 mt-2 border-t border-border"
                      data-testid="withdraw-purpose-code-row"
                    >
                      <SummaryRow label="RBI purpose code" value={pc.code} mono />
                      <p className="text-xs text-muted-foreground mt-1 leading-snug">
                        Auto-selected because you chose{" "}
                        {paymentType === "advance" ? "advance payment" : "balance / final payment"}.{" "}
                        {pc.code}: {pc.description}.
                      </p>
                    </div>
                  </div>
                );
              })()}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={confirm}
                  disabled={confirming}
                  className="h-14 px-6 rounded-lg font-semibold flex-1"
                  data-testid="withdraw-confirm-initiate-btn"
                >
                  {confirming ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Initiating…
                    </>
                  ) : (
                    <>Confirm &amp; initiate payout</>
                  )}
                </Button>
                <Button
                  onClick={() => setStep("review")}
                  variant="outline"
                  className="h-14 px-6 rounded-lg font-semibold"
                  data-testid="withdraw-goback-edit-btn"
                >
                  Go back and edit
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "processing" && (
          <Card>
            <CardContent className="p-12 text-center space-y-4">
              <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin" />
              <p className="font-display text-lg font-bold">Reconciling with your invoice…</p>
              <p className="text-sm text-muted-foreground">Uploading document · Creating receivable · Initiating payout</p>
            </CardContent>
          </Card>
        )}

        {step === "done" && result && (
          <Card className="border-primary/30 bg-primary/5" data-testid={WITHDRAW.successBanner}>
            <CardContent className="p-6 md:p-8 space-y-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[#E5F4EC] grid place-items-center">
                  <CheckCircle2 className="h-6 w-6 text-[#1E8A5B]" />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold text-primary">Withdrawal initiated</h2>
                  <p className="text-sm text-muted-foreground">Rupees will land in your bank shortly.</p>
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <SummaryRow label="Payout amount" value={inr(result.payout?.amount_inr || 0)} mono />
                <SummaryRow label="To account" value={result.payout?.beneficiary_masked} mono />
                <SummaryRow label="IFSC" value={result.payout?.ifsc} mono />
                <SummaryRow label="Reference" value={result.payout?.reference} mono />
                <SummaryRow label="Expected by" value={new Date(result.payout?.expected_arrival).toLocaleString()} />
                <SummaryRow label="Receivable ID" value={result.xflow?.receivable_id} mono />
                <SummaryRow
                  label="RBI purpose code"
                  value={result.purpose_code || purposeCodeFor(paymentType).code}
                  mono
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <Button
                  onClick={() => navigate("/dashboard")}
                  variant="outline"
                  className="h-14 font-semibold rounded-lg"
                  data-testid={WITHDRAW.goBackBtn}
                >
                  Back to dashboard
                </Button>
                <Button
                  onClick={() => navigate(`/transactions/${txnId}`)}
                  className="h-14 font-semibold rounded-lg"
                  data-testid="withdraw-view-txn-btn"
                >
                  View transaction & eBRC status
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Post-withdrawal action card — EDPMS closure via HDFC Bank */}
        {step === "done" && result && (
          <Card className="border" data-testid={WITHDRAW.actionCard}>
            <CardContent className="p-6 md:p-8 space-y-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-secondary/15 grid place-items-center shrink-0">
                  <Mail className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-primary">
                    Action Required: Complete EDPMS Closure
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-2xl">
                    Your JP Morgan Payment Advice will be available for download within 1 business day. Once
                    available, email it to your bank&apos;s trade finance team with your shipping bill number so
                    they can close your EDPMS entry and generate your eBRC.
                  </p>
                </div>
              </div>

              {!showEmail ? (
                <Button
                  onClick={() => setShowEmail(true)}
                  className="h-12 px-6 rounded-lg font-semibold bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                  data-testid={WITHDRAW.generateEmailBtn}
                >
                  <Mail className="h-4 w-4 mr-2" /> Generate {bankName} Email Draft
                </Button>
              ) : (
                <div className="space-y-4">
                  <div
                    className="rounded-lg border bg-muted/40 p-5 space-y-3"
                    data-testid={WITHDRAW.emailPreview}
                  >
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">
                        Subject
                      </p>
                      <p className="font-medium text-foreground break-all">{emailSubject()}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
                        Body
                      </p>
                      <pre className="whitespace-pre-wrap text-sm text-foreground font-sans leading-relaxed">
                        {emailBody()}
                      </pre>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={copyEmail}
                      variant="outline"
                      className="h-12 px-6 rounded-lg font-semibold"
                      data-testid={WITHDRAW.copyEmailBtn}
                    >
                      <Copy className="h-4 w-4 mr-2" /> Copy Email
                    </Button>
                    <Button
                      onClick={downloadAdvice}
                      className="h-12 px-6 rounded-lg font-semibold"
                      data-testid={WITHDRAW.downloadAdviceBtn}
                    >
                      <Download className="h-4 w-4 mr-2" /> Download Payment Advice PDF
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

function Stepper({ step }) {
  const items = [
    { key: "upload", label: "Upload invoice" },
    { key: "review", label: "Review details" },
    { key: "done", label: "Payout initiated" },
  ];
  const idx = { upload: 0, review: 1, processing: 2, done: 2 }[step] ?? 0;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {items.map((it, i) => (
        <div key={it.key} className="flex items-center gap-3">
          <div
            className={`h-8 w-8 rounded-full grid place-items-center text-sm font-semibold border ${
              i <= idx
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-white text-muted-foreground border-border"
            }`}
          >
            {i < idx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
          </div>
          <span className={`text-sm ${i <= idx ? "text-foreground font-medium" : "text-muted-foreground"}`}>
            {it.label}
          </span>
          {i < items.length - 1 && <div className="hidden md:block w-8 h-px bg-border" />}
        </div>
      ))}
    </div>
  );
}

function FieldRow({ label, value, onChange, testid, placeholder, hint, type = "text" }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
        {label} <span className="text-red-500">*</span>
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-12"
        data-testid={testid}
      />
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function SummaryRow({ label, value, mono, sublabel }) {
  return (
    <div className="flex justify-between items-start gap-2 py-2 border-b last:border-0">
      <span className="text-muted-foreground uppercase tracking-wider text-xs font-medium">{label}</span>
      <div className="text-right shrink-0">
        <span className={`block font-medium text-foreground break-all ${mono ? "sf-mono" : ""}`}>{value || "—"}</span>
        {sublabel && (
          <span className="block text-[10px] text-muted-foreground/80 mt-0.5 font-medium">{sublabel}</span>
        )}
      </div>
    </div>
  );
}
